// Unpublished Work © 2023-2024 Deere & Company.

import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { FeatureToggleApi } from "../../api/featureToggle/featureToggleApi";
import FeatureToggleApiV2 from "../../api/featureToggle/featureToggleApiV2";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import {
  ADDITIONAL_FIELDS,
  CategoryKey,
  ToggleKey,
} from "../../pages/featureToggles/constants";
import {
  AdditionalFields,
  FeatureToggleDetail,
  FeatureToggleDetailExtended,
  FeatureToggleMap,
  FeatureToggleMapExtended,
  FeatureToggleStatus,
  FeatureToggleStatusMap,
  SelectedValue,
  SelectedValueExtended,
  ToggleLevel,
  UpdateTogglesErrorMap,
  UpdateTogglesResult,
} from "../../pages/featureToggles/types";
import { isAuthorizedForMultipleAccounts } from "../../pages/preferences/utils";
import { ConfigContext } from "../ConfigContext";
import { DealerAccountContext } from "../DealerAccountContext";
import { RBACAccessContext } from "../RBACAccessContext/RBACAccessContext";
import { isFeatureToggleRBACEnabled } from "../RBACAccessContext/RBACAcessUtil";
import { UserContext } from "../UserContext/UserContext";

/*
  To complete migration to FeatureToggleContextV2, delete the
   `FeatureToggleContext` dir and rename this dir and it's file to
    `FeatureToggleContext`

    Imp files to update:
    - privateRoutes.tsx
    - useNavigationItems.tsx
    - FeatureToggles.tsx
    - PromotionDetails.tsx
    - PromotionList.tsx
    - render.tsx
    - renderHook.tsx

    Remove the `ENABLE_FEATURE_TOGGLE_CONTEXT_V2` toggle
*/

const DEFAULT_FEATURE_TOGGLES: FeatureToggleDetail[] = [];

const DEFAULT_ALL_FEATURE_TOGGLES_STATUS: FeatureToggleStatus[] = [];

export interface FeatureTogglesContextProps {
  featureToggleMap: FeatureToggleMapExtended;
  updateFeatureToggleMap: (map: FeatureToggleMapExtended) => void;
  isErrorOccurred: boolean;
  originalFeatureToggleMap: FeatureToggleMapExtended;
  saveFeatureToggles: () => Promise<void>;
  updateFeatureToggles: (
    featureToggleUpdates: FeatureToggleMapExtended
  ) => Promise<UpdateTogglesResult>;
  reloadFeatureToggles: () => void;
  isToggleEnabled: (featureName: string, toggleName: string) => boolean;
  additionalFieldsValues: (
    featureName: string,
    toggleName: string
  ) => Map<string, string[]> | undefined;
  isToggleEnabledWithAccountId: (
    categoryKey: CategoryKey,
    toggleKey: ToggleKey,
    country: string,
    account: string
  ) => Promise<boolean>;
}

export const FeatureToggleContextV2 = createContext<FeatureTogglesContextProps>(
  {} as FeatureTogglesContextProps
);
export const useFeatureToggleContextV2 = () =>
  useContext(FeatureToggleContextV2);

const sortByCategoryNameAsc = (
  data: FeatureToggleDetail[]
): FeatureToggleDetail[] =>
  [...data].sort((featureToggle1, featureToggle2) => {
    if (featureToggle1.categoryName !== featureToggle2.categoryName)
      return featureToggle1.categoryName.localeCompare(
        featureToggle2.categoryName
      );
    return featureToggle1.name.localeCompare(featureToggle2.name);
  });

export const createFeatureToggleMap = (
  response: FeatureToggleDetail[]
): Map<string, FeatureToggleDetailExtended[]> => {
  const map: FeatureToggleMapExtended = new Map();
  const sortedFeatureToggles = sortByCategoryNameAsc(response);
  sortedFeatureToggles.forEach((feature: FeatureToggleDetail) => {
    const featureName = feature.categoryKey;
    const featureWithNames: FeatureToggleDetailExtended = {
      ...feature,
      // when the display names are not there then use the current names
      name: feature.name ?? feature.key,
      categoryName: feature.categoryName ?? feature.categoryKey,
      selectedValues: feature.selectedValues?.map((value) => ({
        ...value,
        isSaved: true,
      })),
    };
    if (map.has(featureName)) map.get(featureName)!.push(featureWithNames);
    else map.set(featureName, [featureWithNames]);
  });
  return map;
};

export const createFeatureToggleStatusMap = (
  response: FeatureToggleStatus[]
): FeatureToggleStatusMap => {
  const map: FeatureToggleStatusMap = new Map();
  response.forEach((feature: FeatureToggleStatus) => {
    const featureName = feature.categoryKey;
    if (map.has(featureName)) map.get(featureName)!.push(feature);
    else map.set(featureName, [feature]);
  });
  return map;
};

const isValueSelected = (value: string, selectedValue: SelectedValue) => {
  if (selectedValue.enabledFor.includes(value)) return true;

  return (
    selectedValue.enabledFor.length === 0 &&
    !selectedValue.disabledFor.includes(value)
  );
};

export const additionalFieldsValues =
  (
    featureMap: FeatureToggleMapExtended | undefined,
    dealerAccountId: string | undefined,
    dealerLocation: string | undefined,
    mruSalesOrg: string | undefined
  ) =>
  (featureName: string, toggleName: string) => {
    if (
      featureMap === undefined ||
      dealerAccountId === undefined ||
      dealerLocation === undefined ||
      mruSalesOrg === undefined
    )
      return undefined;

    const feature = featureMap?.get(featureName);

    if (feature === undefined) return undefined;

    const toggle = feature?.find(({ key }) => key === toggleName);

    if (toggle === undefined) return undefined;

    const selectedValue = toggle.selectedValues?.find(
      (value) => value.country === dealerLocation
    );

    if (selectedValue === undefined) return undefined;

    if (selectedValue?.additionalValues === undefined) return undefined;

    const selectedValuesInAdditional = selectedValue?.additionalValues;

    return new Map(
      selectedValuesInAdditional.map((additional) => [
        additional.key,
        additional.values,
      ])
    );
  };

export const isToggleEnabledFactory =
  (
    featureMap: FeatureToggleMapExtended | undefined,
    dealerAccountId: string | undefined,
    dealerLocation: string | undefined,
    mruSalesOrg: string | undefined
  ) =>
  (featureName: string, toggleName: string) => {
    if (
      featureMap === undefined ||
      dealerAccountId === undefined ||
      dealerLocation === undefined ||
      mruSalesOrg === undefined
    )
      return false;

    const feature = featureMap?.get(featureName);

    if (feature === undefined) return false;

    const toggle = feature?.find(({ key }) => key === toggleName);

    if (toggle === undefined) return false;

    if (!toggle.enabled) return false;

    const selectedValue = toggle.selectedValues?.find(
      (value) => value.country === dealerLocation
    );

    if (selectedValue === undefined) return false;

    if (selectedValue?.level === ToggleLevel.COUNTRY) return true;

    if (selectedValue.level === ToggleLevel.BRANCH) {
      return isValueSelected(mruSalesOrg, selectedValue);
    }
    return isValueSelected(dealerAccountId, selectedValue);
  };

export const toggleStatusFactory =
  (featureToggleStatusMap: FeatureToggleStatusMap | undefined) =>
  (featureName: string, toggleName: string) => {
    if (featureToggleStatusMap === undefined) return false;
    const feature = featureToggleStatusMap?.get(featureName);
    if (feature === undefined) return false;
    const toggle = feature?.find(({ key }) => key === toggleName);
    if (toggle === undefined) return false;
    return toggle.enabled;
  };

export function featureTogglesLoader(
  url: string,
  accessToken: string,
  isFeatureToggleApiAuthEnable: boolean
) {
  const featureToggleApi = FeatureToggleApiV2();
  return isFeatureToggleApiAuthEnable
    ? featureToggleApi.getFeatureToggles()
    : FeatureToggleApi.getFeatureToggles(url, accessToken);
}

export const additionalFieldsValuesFromAllToggleStatus =
  (featureToggleStatusMap: FeatureToggleStatusMap | undefined) =>
  (featureName: string, toggleName: string) => {
    if (featureToggleStatusMap === undefined) return undefined;
    const feature = featureToggleStatusMap?.get(featureName);
    if (feature === undefined) return undefined;
    const toggle = feature?.find(({ key }) => key === toggleName);
    if (toggle === undefined) return undefined;
    if (toggle.additionalValues === undefined) return undefined;
    const additionalValueList = toggle.additionalValues ?? [];
    return new Map(
      additionalValueList.map((additional) => [
        additional.key,
        additional.values,
      ])
    );
  };

function filterInvalidSelectedValues(
  toggle: FeatureToggleDetailExtended
): FeatureToggleDetail {
  const selectedValues = toggle.selectedValues as SelectedValueExtended[];
  const validSelectedValuesExtended = selectedValues.filter((selectedValue) => {
    const isCountryFieldValueValid =
      Boolean(selectedValue.country) ||
      selectedValue.level !== ToggleLevel.COUNTRY;
    const isIdFieldValueValid =
      (selectedValue.enabledFor.length > 0 &&
        Boolean(selectedValue.enabledFor[0])) ||
      (selectedValue.disabledFor.length > 0 &&
        Boolean(selectedValue.disabledFor[0]));
    const isAdditionalFieldValueValid =
      selectedValue.additionalValues?.some(
        (additional) =>
          additional.key === ADDITIONAL_FIELDS.ORDER_TYPE &&
          additional.values.length > 0
      ) ?? false;
    return (
      isCountryFieldValueValid ||
      isIdFieldValueValid ||
      isAdditionalFieldValueValid
    );
  });
  const validSelectedValues: SelectedValue[] = validSelectedValuesExtended.map(
    (selectedValue) => {
      // eslint-disable-next-line
      const { isSaved, ...rest } = selectedValue;
      return rest;
    }
  );

  const enabled = validSelectedValues.length === 0 ? false : toggle.enabled;
  return {
    ...toggle,
    enabled,
    selectedValues: enabled ? validSelectedValues : [],
  };
}

function cleanFeatureToggleUpdates(
  featureToggleUpdates: FeatureToggleMapExtended
): FeatureToggleMap {
  return new Map(
    Array.from(featureToggleUpdates.entries()).map(([categoryKey, toggles]) => {
      const cleanedFeatureToggles = toggles.map((toggle) =>
        filterInvalidSelectedValues(toggle)
      );
      return [categoryKey, cleanedFeatureToggles];
    })
  );
}

function updateToggleWithUpdationStatus(
  toggle: FeatureToggleDetail,
  isUpdateSuccess: boolean
): FeatureToggleDetailExtended {
  return {
    ...toggle,
    selectedValues: toggle.selectedValues.map((selectedValue) => ({
      ...selectedValue,
      isSaved: isUpdateSuccess,
    })),
  };
}

function storeToggle(
  store: FeatureToggleMapExtended,
  categoryKey: string,
  toggle: FeatureToggleDetailExtended
) {
  if (!store.has(categoryKey)) {
    store.set(categoryKey, []);
  }
  store.get(categoryKey)!.push(toggle);
}

function storeToggleUpdateError(
  store: UpdateTogglesErrorMap,
  categoryKey: string,
  toggleKey: string,
  error?: any
) {
  if (!store.has(categoryKey)) {
    store.set(categoryKey, []);
  }
  store.get(categoryKey)!.push({ toggleKey, error });
}

function updateFeatureMapWithUpdatedToggles(
  featureMap: FeatureToggleMapExtended,
  updatedToggleMap: FeatureToggleMapExtended
) {
  const updatedFeatureMap = new Map(featureMap);

  Array.from(updatedToggleMap.entries()).forEach(([categoryKey, toggles]) => {
    const existingToggles = updatedFeatureMap.get(categoryKey)!;
    const updatedToggles = existingToggles.map((toggle) => {
      const updatedToggle = toggles.find(({ key }) => key === toggle.key);
      return updatedToggle ?? toggle;
    });
    updatedFeatureMap.set(categoryKey, updatedToggles);
  });
  return updatedFeatureMap;
}

export const FeatureToggleProviderV2 = ({
  children,
  initialValue,
  isErrorOccurred: isErrorOccurredInInitialCall = false,
}: {
  children: ReactElement;
  initialValue?: any;
  isErrorOccurred?: boolean;
}) => {
  const { authState, oktaAuth } = useOktaAuth();
  const getAccessToken = (): string => {
    const authToken = oktaAuth.getAccessToken();
    if (!authToken) return "";
    return authToken;
  };

  const {
    FEATURE_TOGGLE_SERVICE_URL,
    ENABLE_FT_RBAC_INTEGRATION,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_FEATURE_TOGGLE_SERVICE,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);
  const {
    ENABLE_FEATURE_TOGGLE_SERVICE_INTEGRATION,
    ENABLE_ALL_FEATURE_TOGGLE_STATUS,
  } = useContext(ConfigContext);
  const { dealerAccount, corporateManagerSetupIncomplete } =
    useContext(DealerAccountContext);

  const userDetails = useContext(UserContext);

  const featureToggleApi = FeatureToggleApiV2();

  const isFeatureToggleApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT &&
    ENABLE_RBAC_AUTH_SUPPORT_FOR_FEATURE_TOGGLE_SERVICE;

  const getInitialFilterMap = () =>
    initialValue === undefined && !isErrorOccurredInInitialCall
      ? undefined
      : createFeatureToggleMap(initialValue ?? DEFAULT_FEATURE_TOGGLES);

  const [featureMap, setFeatureMap] = useState<
    FeatureToggleMapExtended | undefined
  >(getInitialFilterMap());
  const [featureTogglesStatusMap, setAllFeatureToggleStatusMap] = useState<
    FeatureToggleStatusMap | undefined
  >(createFeatureToggleStatusMap(DEFAULT_ALL_FEATURE_TOGGLES_STATUS));
  const [originalMap, setOriginalMap] = useState<
    FeatureToggleMapExtended | undefined
  >(getInitialFilterMap());
  const [isErrorOccurred, setIsErrorOccurred] = useState(
    isErrorOccurredInInitialCall
  );
  const { featureToggleDetails } = useContext(RBACAccessContext); // need to verify

  const fetchFeatureToggles = (token: string) =>
    !ENABLE_FEATURE_TOGGLE_SERVICE_INTEGRATION || dealerAccount === undefined
      ? Promise.resolve(DEFAULT_FEATURE_TOGGLES)
      : featureToggleDetail(token);

  function getFeatureToggles(token: string) {
    return isFeatureToggleApiAuthEnable
      ? featureToggleApi.getFeatureToggles()
      : FeatureToggleApi.getFeatureToggles(FEATURE_TOGGLE_SERVICE_URL, token);
  }

  function saveFeatureTogglesData(feature: {
    additionalFields?: AdditionalFields[];
    selectedValues: any;
    name: string;
    categoryKey: string;
    categoryName: string;
    key: string;
    enabled: boolean;
  }) {
    return isFeatureToggleApiAuthEnable
      ? featureToggleApi.saveFeatureToggles(feature)
      : FeatureToggleApi.saveFeatureToggles(
          FEATURE_TOGGLE_SERVICE_URL,
          feature,
          authState?.accessToken?.accessToken as string
        );
  }

  const allFeatureToggleStatusDetails = (
    token: string,
    country: string,
    branch?: string,
    accountId?: string
  ) =>
    isFeatureToggleApiAuthEnable
      ? featureToggleApi.getAllFeatureToggleStatus(country, branch, accountId)
      : FeatureToggleApi.getAllFeatureToggleStatus(
          FEATURE_TOGGLE_SERVICE_URL,
          token,
          country,
          branch,
          accountId
        );

  const featureToggleDetail = (token: string) =>
    isFeatureToggleRBACEnabled(ENABLE_FT_RBAC_INTEGRATION)
      ? featureToggleDetails
      : getFeatureToggles(token);

  const fetchAllFeatureToggleStatus = (token: string) =>
    !ENABLE_ALL_FEATURE_TOGGLE_STATUS ||
    dealerAccount === undefined ||
    dealerAccount.location === undefined
      ? Promise.resolve(DEFAULT_ALL_FEATURE_TOGGLES_STATUS)
      : allFeatureToggleStatusDetails(
          token,
          dealerAccount?.location?.country,
          dealerAccount?.mruSalesOrg,
          dealerAccount?.id
        );

  const loadAllFeatureToggleStatus = (accessToken: string) =>
    fetchAllFeatureToggleStatus(accessToken)
      .then((data) => {
        const map = createFeatureToggleStatusMap(data);
        setAllFeatureToggleStatusMap(new Map(map));
        setIsErrorOccurred(false);
      })
      .catch(() => {
        setIsErrorOccurred(true);
        const map = createFeatureToggleStatusMap(
          DEFAULT_ALL_FEATURE_TOGGLES_STATUS
        );
        setAllFeatureToggleStatusMap(map);
      });

  const saveFeatureToggles = () => {
    const featureToggles = Array.from(featureMap!.values()).flat();
    const cleanedFeatureToggles = featureToggles.map((toggle) =>
      filterInvalidSelectedValues(toggle)
    );

    const promises = cleanedFeatureToggles.map((feature) =>
      saveFeatureTogglesData(feature)
    );

    return Promise.all(promises).then((responses) => {
      const map = createFeatureToggleMap(responses);
      setOriginalMap(new Map(map));
      setFeatureMap(new Map(map));
      return loadAllFeatureToggleStatus(
        authState?.accessToken?.accessToken as string
      );
    });
  };

  function updateFeatureToggleDetailPromise(
    toggleDetails: FeatureToggleDetail
  ) {
    return isFeatureToggleApiAuthEnable
      ? featureToggleApi.saveFeatureToggles(toggleDetails)
      : FeatureToggleApi.saveFeatureToggles(
          FEATURE_TOGGLE_SERVICE_URL,
          toggleDetails,
          authState?.accessToken?.accessToken as string
        );
  }

  const updateFeatureToggles = async (
    featureToggleUpdates: FeatureToggleMapExtended
  ): Promise<UpdateTogglesResult> => {
    const cleanedUpdates = cleanFeatureToggleUpdates(featureToggleUpdates);
    const flattenedUpdates = Array.from(cleanedUpdates.values()).flat();
    const successfulToggleUpdates: FeatureToggleMapExtended = new Map();
    const erroredToggleUpdates: FeatureToggleMapExtended = new Map();
    const errors: UpdateTogglesErrorMap = new Map();

    const updatePromises = flattenedUpdates.map((toggle) =>
      updateFeatureToggleDetailPromise(toggle)
        .then((returnedToggleDetail) => {
          const toggleWithSavedStatus = updateToggleWithUpdationStatus(
            returnedToggleDetail,
            true
          );
          storeToggle(
            successfulToggleUpdates,
            toggleWithSavedStatus.categoryKey,
            toggleWithSavedStatus
          );
        })
        .catch((error) => {
          storeToggleUpdateError(errors, toggle.categoryKey, toggle.key, error);
          const toggleWithErrorStatus = updateToggleWithUpdationStatus(
            toggle,
            false
          );
          storeToggle(
            erroredToggleUpdates,
            toggleWithErrorStatus.categoryKey,
            toggleWithErrorStatus
          );
        })
    );
    await Promise.allSettled(updatePromises);
    const existingOriginalMap = originalMap ?? new Map();
    const featureMapWithSuccessfulUpdates = updateFeatureMapWithUpdatedToggles(
      existingOriginalMap,
      successfulToggleUpdates
    );
    setOriginalMap(featureMapWithSuccessfulUpdates);
    const featureMapWithAllUpdates = updateFeatureMapWithUpdatedToggles(
      featureMapWithSuccessfulUpdates,
      erroredToggleUpdates
    );
    setFeatureMap(featureMapWithAllUpdates);
    loadAllFeatureToggleStatus(authState?.accessToken?.accessToken as string);
    return {
      updatedOriginalFeatureMap: featureMapWithSuccessfulUpdates,
      updatedFeatureMap: featureMapWithAllUpdates,
      errors,
    };
  };

  const setFeatureToggleData = (data: FeatureToggleDetail[]) => {
    const map = createFeatureToggleMap(data);
    setOriginalMap(new Map(map));
    setFeatureMap(new Map(map));
  };

  const setAllFeatureToggleStatusData = (data: FeatureToggleStatus[]) => {
    const map = createFeatureToggleStatusMap(data);
    setAllFeatureToggleStatusMap(new Map(map));
  };

  const loadFeatureToggleDetails = async () => {
    Promise.all([
      fetchFeatureToggles(authState!.accessToken!.accessToken),
      fetchAllFeatureToggleStatus(authState!.accessToken!.accessToken),
    ])
      .then(([featureTogglesResult, featureToggleStatusResult]) => {
        setFeatureToggleData(featureTogglesResult as FeatureToggleDetail[]);
        setAllFeatureToggleStatusData(
          featureToggleStatusResult as FeatureToggleStatus[]
        );
      })
      .catch(() => {
        setIsErrorOccurred(true);
        setFeatureToggleData(DEFAULT_FEATURE_TOGGLES);
        setAllFeatureToggleStatusData(DEFAULT_ALL_FEATURE_TOGGLES_STATUS);
      });
  };

  const isToggleEnabledWithAccountId = async (
    categoryKey: CategoryKey,
    toggleKey: ToggleKey,
    country: string,
    account: string
  ) => {
    if (isFeatureToggleApiAuthEnable) {
      const featureToggleStatus = await featureToggleApi.getFeatureToggleStatus(
        categoryKey,
        toggleKey,
        country,
        account
      );
      return featureToggleStatus.enabled;
    }

    const featureToggleStatus = await FeatureToggleApi.getFeatureToggleStatus(
      FEATURE_TOGGLE_SERVICE_URL,
      getAccessToken(),
      categoryKey,
      toggleKey,
      country,
      account
    );

    return featureToggleStatus.enabled;
  };

  useEffect(() => {
    if (
      authState &&
      authState.isAuthenticated &&
      authState.accessToken?.accessToken &&
      !initialValue &&
      !isErrorOccurred &&
      dealerAccount?.id
    ) {
      loadFeatureToggleDetails();
    }
  }, [
    authState,
    initialValue,
    isErrorOccurredInInitialCall,
    dealerAccount?.id,
  ]);

  const isCorporateManagerSetupIncomplete =
    isAuthorizedForMultipleAccounts(
      userDetails,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    ) && corporateManagerSetupIncomplete;

  const isJdRoleSetupIsInComplete =
    userDetails?.isUserWithJDRole() && !dealerAccount?.id;

  const shouldWaitForApiCalls = !(
    isCorporateManagerSetupIncomplete || isJdRoleSetupIsInComplete
  );

  if (featureMap === undefined && !isErrorOccurred && shouldWaitForApiCalls) {
    return <LoadingIndicator color="#367c2b" />;
  }

  if (
    ENABLE_ALL_FEATURE_TOGGLE_STATUS &&
    featureTogglesStatusMap === undefined &&
    !isErrorOccurred &&
    shouldWaitForApiCalls
  ) {
    return <LoadingIndicator color="#367c2b" />;
  }

  return (
    <FeatureToggleContextV2.Provider
      value={{
        featureToggleMap: featureMap as FeatureToggleMapExtended,
        updateFeatureToggleMap: setFeatureMap,
        originalFeatureToggleMap: originalMap as FeatureToggleMapExtended,
        isErrorOccurred,
        saveFeatureToggles,
        updateFeatureToggles,
        reloadFeatureToggles: () => {
          loadFeatureToggleDetails();
        },
        isToggleEnabled: ENABLE_ALL_FEATURE_TOGGLE_STATUS
          ? toggleStatusFactory(featureTogglesStatusMap)
          : isToggleEnabledFactory(
              originalMap,
              dealerAccount?.id,
              dealerAccount?.location?.country,
              dealerAccount?.mruSalesOrg
            ),
        additionalFieldsValues: ENABLE_ALL_FEATURE_TOGGLE_STATUS
          ? additionalFieldsValuesFromAllToggleStatus(featureTogglesStatusMap)
          : additionalFieldsValues(
              originalMap,
              dealerAccount?.id,
              dealerAccount?.location?.country,
              dealerAccount?.mruSalesOrg
            ),
        isToggleEnabledWithAccountId,
      }}
    >
      {children}
    </FeatureToggleContextV2.Provider>
  );
};
